// src/pages/Campaigns/VolunteerManagement/EventManagement.js

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, FormControl, InputLabel, Select, MenuItem, Grid,
  Alert, Pagination, CircularProgress, Stack, Card, CardContent, Tooltip,
  InputAdornment, Checkbox
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Campaign as CampaignIcon, People as PeopleIcon, AccessTime, Search as SearchIcon,
  CalendarToday as CalendarIcon, CalendarMonth as CalendarViewIcon,
  CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
  RemoveCircleOutline as UntrainIcon, Article as ArticleIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import {
  EventType,
  EventTypeLabels,
  EventStatus,
  EventStatusLabels
} from './volunteerEnums';

const EventManagement = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchName, setSearchName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  
  const [eventDialog, setEventDialog] = useState({ open: false, event: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Calendar
  const [calendarConnected, setCalendarConnected] = useState(false);

  // NEW: Selected events for training
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [isTraining, setIsTraining] = useState(false);
  const [isUntraining, setIsUntraining] = useState(false);

  // NEW: Timezones state
  const [timezones, setTimezones] = useState([]);
  
  // Form data for multi-day events with time slots
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_type: '',
    location: '',
    event_start_date: '',
    event_end_date: '',
    event_start_time: '',
    event_end_time: '',
    timezone: 'UTC', // NEW: Default timezone
    time_slots: [
      {
        slot_name: 'Morning Shift',
        start_time: '06:00',
        end_time: '09:00',
        required_volunteers: 5
      }
    ]
  });

  // NEW: Load timezones on mount
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        const response = await axiosInstance.get('/api/selections/timezones');
        setTimezones(response.data.data);
      } catch (error) {
        console.error('Error loading timezones:', error);
      }
    };
    
    loadTimezones();
  }, []);

  useEffect(() => {
    const checkCalendarStatus = async () => {
      try {
        const response = await axiosInstance.get(`/api/event-calendar/status`, {
          params: { chatbot_id: chatbotId }
        });
        setCalendarConnected(response.data.connected);
      } catch (error) {
        console.error('Error checking calendar status:', error);
      }
    };
    
    if (chatbotId) {
      checkCalendarStatus();
    }
  }, [chatbotId]);

  const handleConnectCalendar = async () => {
    try {
      const response = await axiosInstance.get(`/api/event-calendar/auth/start`, {
        params: { chatbot_id: chatbotId }
      });
      
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      showSnackbar('Failed to connect calendar', 'error');
    }
  };

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const viewPerm = await hasPermission('volunteer_management', 'view');
        const modifyPerm = await hasPermission('volunteer_management', 'add|edit');
        const deletePerm = await hasPermission('volunteer_management', 'delete');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView && chatbotId) {
      const timeoutId = setTimeout(() => {
        loadEvents();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [canView, chatbotId, page, statusFilter, typeFilter, searchName, startDate, endDate]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const params = {
        chatbot_id: chatbotId,
        page,
        per_page: 10
      };
      
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.event_type = typeFilter;
      if (searchName) params.search = searchName;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await axiosInstance.get(`/api/events`, { params });
      
      setEvents(response.data.data);
      setTotalPages(response.data.pagination.pages);
      // Clear selection when events reload
      setSelectedEvents([]);
    } catch (error) {
      showSnackbar('Error loading events', 'error');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Handle select all checkbox
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedEvents(events.map(e => e.id));
    } else {
      setSelectedEvents([]);
    }
  };

  // NEW: Handle individual checkbox
  const handleSelectEvent = (eventId) => {
    setSelectedEvents(prev => {
      if (prev.includes(eventId)) {
        return prev.filter(id => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };

  // NEW: Check if event is selected
  const isEventSelected = (eventId) => selectedEvents.includes(eventId);

  // NEW: Train selected events
  const handleTrainSelectedEvents = async () => {
    if (selectedEvents.length === 0) {
      showSnackbar('Please select events to train', 'warning');
      return;
    }

    setIsTraining(true);
    try {
      const response = await axiosInstance.post('/api/events/training/train',
        { event_ids: selectedEvents, retrain_all: true },
        { params: { chatbot_id: chatbotId } }
      );

      if (response.data.success) {
        showSnackbar(response.data.message, 'success');
        setSelectedEvents([]);
        loadEvents(); // Reload to show updated is_trained status
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error training events', 'error');
    } finally {
      setIsTraining(false);
    }
  };

  // NEW: Check if any selected event is trained
  const hasSelectedTrainedEvents = () => {
    return selectedEvents.some(eventId => {
      const event = events.find(e => e.id === eventId);
      return event && event.is_trained;
    });
  };

  // NEW: Check if any selected event is untrained
  const hasSelectedUntrainedEvents = () => {
    return selectedEvents.some(eventId => {
      const event = events.find(e => e.id === eventId);
      return event && !event.is_trained;
    });
  };

  // NEW: Untrain selected events (remove from vector DB)
  const handleUntrainSelectedEvents = async () => {
    const trainedEventIds = selectedEvents.filter(eventId => {
      const event = events.find(e => e.id === eventId);
      return event && event.is_trained;
    });

    if (trainedEventIds.length === 0) {
      showSnackbar('No trained events selected', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${trainedEventIds.length} event(s) from training? This will delete them from the vector database.`)) {
      return;
    }

    setIsUntraining(true);
    try {
      const response = await axiosInstance.post('/api/events/training/untrain',
        { event_ids: trainedEventIds },
        { params: { chatbot_id: chatbotId } }
      );

      if (response.data.success) {
        showSnackbar(response.data.message, 'success');
        setSelectedEvents([]);
        loadEvents();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error removing events from training', 'error');
    } finally {
      setIsUntraining(false);
    }
  };

  const handleSaveEvent = async () => {
    // Validate dates
    if (formData.event_end_date < formData.event_start_date) {
      showSnackbar('End date must be after start date', 'error');
      return;
    }

    // Validate time slots
    if (formData.time_slots.length === 0) {
      showSnackbar('Please add at least one time slot', 'error');
      return;
    }

    for (const slot of formData.time_slots) {
      if (!slot.slot_name || !slot.start_time || !slot.end_time || !slot.required_volunteers) {
        showSnackbar('All time slot fields are required', 'error');
        return;
      }
    }

    // NEW: Validate timezone
    if (!formData.timezone) {
      showSnackbar('Please select a timezone', 'error');
      return;
    }

    try {
      let savedEvent;
      if (eventDialog.event) {
        await axiosInstance.put(`/api/events`, formData, {
          params: {
            event_id: eventDialog.event.id,
            chatbot_id: chatbotId
          }
        });
        savedEvent = eventDialog.event;
        showSnackbar('Event updated successfully', 'success');
        
        // NEW: Auto-update vector DB if event is already trained
        if (eventDialog.event.is_trained) {
          try {
            await axiosInstance.post(`/api/events/training/${eventDialog.event.id}`,
              {},
              { params: { chatbot_id: chatbotId } }
            );
            showSnackbar('Training data updated', 'success');
          } catch (trainError) {
            console.error('Failed to update training data:', trainError);
            showSnackbar('Event updated but training data update failed', 'warning');
          }
        }
      } else {
        const response = await axiosInstance.post(`/api/events`, formData, {
          params: { chatbot_id: chatbotId }
        });
        savedEvent = response.data.data;
        showSnackbar('Event created successfully', 'success');
        
        // Create calendar event if connected
        if (calendarConnected) {
          try {
            await axiosInstance.post(`/api/event-calendar/create`, 
              { event_id: savedEvent.id },
              { params: { chatbot_id: chatbotId } }
            );
            showSnackbar('Calendar invite created', 'success');
          } catch (calError) {
            console.error('Failed to create calendar event:', calError);
            showSnackbar('Event created but calendar invite failed', 'warning');
          }
        }
      }
      
      handleCloseDialog();
      loadEvents();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving event', 'error');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axiosInstance.delete(`/api/events`, {
        params: {
          event_id: eventId,
          chatbot_id: chatbotId
        }
      });
      showSnackbar('Event deleted successfully', 'success');
      loadEvents();
    } catch (error) {
      showSnackbar('Error deleting event', 'error');
    }
  };

  const handleOpenDialog = (event = null) => {
    if (event) {
      setFormData({
        name: event.name,
        description: event.description || '',
        event_type: event.event_type,
        location: event.location,
        event_start_date: event.event_start_date,
        event_end_date: event.event_end_date,
        event_start_time: event.event_start_time || '',
        event_end_time: event.event_end_time || '',
        timezone: event.timezone || 'UTC', // NEW
        time_slots: event.time_slots || []
      });
    } else {
      setFormData({
        name: '',
        description: '',
        event_type: '',
        location: '',
        event_start_date: '',
        event_end_date: '',
        event_start_time: '',
        event_end_time: '',
        timezone: 'UTC', // NEW: Default to UTC
        time_slots: [
          {
            slot_name: 'Morning Shift',
            start_time: '06:00',
            end_time: '09:00',
            required_volunteers: 5
          }
        ]
      });
    }
    setEventDialog({ open: true, event });
  };

  const handleCloseDialog = () => {
    setEventDialog({ open: false, event: null });
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const addTimeSlot = () => {
    setFormData({
      ...formData,
      time_slots: [
        ...formData.time_slots,
        {
          slot_name: `Slot ${formData.time_slots.length + 1}`,
          start_time: '',
          end_time: '',
          required_volunteers: 1
        }
      ]
    });
  };

  const removeTimeSlot = (index) => {
    const newSlots = formData.time_slots.filter((_, i) => i !== index);
    setFormData({ ...formData, time_slots: newSlots });
  };

  const updateTimeSlot = (index, field, value) => {
    const newSlots = [...formData.time_slots];
    newSlots[index][field] = value;
    setFormData({ ...formData, time_slots: newSlots });
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'default',
      SCHEDULED: 'info',
      ONGOING: 'warning',
      COMPLETED: 'success',
      CANCELLED: 'error'
    };
    return colors[status] || 'default';
  };

  const handleCreateCampaign = (event) => {
    // Changed from /campaigns/campaign/ to /campaigns/outreach/
    navigate(`/chatbot/${chatbotId}/campaigns/outreach/${event.id}`);
  };

  const handleViewVolunteers = (event) => {
    navigate(`/chatbot/${chatbotId}/campaigns/event-volunteers?event_id=${event.id}`);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setSearchName('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  if (checkingPermissions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 6 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view event management.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Event Management
        </Typography>
        {canModify && (
          <Stack direction="row" spacing={2}>
            {!calendarConnected && canModify && (
              <Button
                variant="outlined"
                startIcon={<CalendarIcon />}
                onClick={handleConnectCalendar}
                color="warning"
              >
                Connect Calendar
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/volunteers`)}
            >
              Add Volunteer
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArticleIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/event-posts`)}
              color="secondary"
            >
              Post Event
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Create Event
            </Button>
            <Button
              variant="outlined"
              startIcon={<CalendarViewIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events/calendar`)}
            >
              Calendar View
            </Button>
          </Stack>
        )}
      </Box>

      {/* Snackbar Alert */}
      {snackbar.open && (
        <Alert 
          severity={snackbar.severity} 
          sx={{ mb: 2 }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      )}

      {/* NEW: Train/Untrain Selected Events Buttons - Only show when events are selected */}
      {selectedEvents.length > 0 && canModify && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`${selectedEvents.length} event(s) selected`} 
            color="primary" 
            variant="outlined"
          />
          {/* Show Train button if any untrained events are selected */}
          {hasSelectedUntrainedEvents() && (
            <Button
              variant="contained"
              color="primary"
              startIcon={isTraining ? <CircularProgress size={20} color="inherit" /> : <ModelTrainingIcon />}
              onClick={handleTrainSelectedEvents}
              disabled={isTraining || isUntraining}
            >
              {isTraining ? 'Training...' : 'Train Selected Events'}
            </Button>
          )}
          {/* Show Untrain button if any trained events are selected */}
          {hasSelectedTrainedEvents() && (
            <Button
              variant="contained"
              color="warning"
              startIcon={isUntraining ? <CircularProgress size={20} color="inherit" /> : <UntrainIcon />}
              onClick={handleUntrainSelectedEvents}
              disabled={isTraining || isUntraining}
            >
              {isUntraining ? 'Removing...' : 'Untrain Selected Events'}
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSelectedEvents([])}
            disabled={isTraining || isUntraining}
          >
            Clear Selection
          </Button>
        </Box>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search Event Name or Location"
                placeholder="Search by name or location..."
                value={searchName}
                onChange={(e) => {
                  setSearchName(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                  <MenuItem value="ONGOING">Ongoing</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="RALLY">Rally</MenuItem>
                  <MenuItem value="DONATION">Donation</MenuItem>
                  <MenuItem value="FUNDRAISING">Fundraising</MenuItem>
                  <MenuItem value="COMMUNITY_SERVICE">Community Service</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            {(statusFilter || typeFilter || searchName || startDate || endDate) && (
              <Grid item xs={12}>
                <Button 
                  size="small" 
                  onClick={clearFilters}
                  sx={{ textTransform: 'none' }}
                >
                  Clear All Filters
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                {/* NEW: Checkbox column */}
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedEvents.length > 0 && selectedEvents.length < events.length}
                    checked={events.length > 0 && selectedEvents.length === events.length}
                    onChange={handleSelectAll}
                    disabled={loading || events.length === 0}
                  />
                </TableCell>
                <TableCell>Event Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Time Slots</TableCell>
                <TableCell>Volunteers</TableCell>
                <TableCell>User Name</TableCell>
                <TableCell>Status</TableCell>
                {/* NEW: Is Trained column */}
                <TableCell>Trained</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Box sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No events found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => (
                  <TableRow 
                    key={event.id} 
                    hover
                    selected={isEventSelected(event.id)}
                  >
                    {/* NEW: Checkbox cell */}
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isEventSelected(event.id)}
                        onChange={() => handleSelectEvent(event.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {event.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={event.event_type} size="small" />
                    </TableCell>
                    <TableCell>
                      {new Date(event.event_start_date).toLocaleDateString()} - {new Date(event.event_end_date).toLocaleDateString()}
                      {event.event_start_time && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {event.event_start_time} - {event.event_end_time}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{event.location}</TableCell>
                    <TableCell>
                      <Chip 
                        icon={<AccessTime />}
                        label={`${event.time_slots?.length || 0} slots`}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {event.total_assigned_volunteers} / {event.total_required_volunteers}
                    </TableCell>
                    <TableCell>
                      {event.created_by_name || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={event.status}
                        color={getStatusColor(event.status)}
                        size="small"
                      />
                    </TableCell>
                    {/* NEW: Is Trained cell */}
                    <TableCell>
                      <Tooltip title={event.is_trained ? 'Event is trained' : 'Event not trained'}>
                        {event.is_trained ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="disabled" fontSize="small" />
                        )}
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View Volunteers">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleViewVolunteers(event)}
                          >
                            <PeopleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Create Campaign">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleCreateCampaign(event)}
                          >
                            <CampaignIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canModify && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(event)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteEvent(event.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
              color="primary"
              sx={{
                '& .Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: '#fff !important',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Event Dialog - Multi-day with time slots and TIMEZONE */}
      <Dialog open={eventDialog.open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {eventDialog.event ? 'Edit Event' : 'Create New Event'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Event Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  label="Event Type"
                >
                  <MenuItem value="RALLY">Rally</MenuItem>
                  <MenuItem value="DONATION">Donation</MenuItem>
                  <MenuItem value="FUNDRAISING">Fundraising</MenuItem>
                  <MenuItem value="COMMUNITY_SERVICE">Community Service</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </Grid>
            
            {/* NEW: Timezone Dropdown */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  label="Timezone"
                >
                  {timezones.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Select the timezone where this event will take place
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Start Date"
                value={formData.event_start_date}
                onChange={(e) => setFormData({ ...formData, event_start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="End Date"
                value={formData.event_end_date}
                onChange={(e) => setFormData({ ...formData, event_end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Event Start Time (Optional)"
                value={formData.event_start_time}
                onChange={(e) => setFormData({ ...formData, event_start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Daily start time for the event"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Event End Time (Optional)"
                value={formData.event_end_time}
                onChange={(e) => setFormData({ ...formData, event_end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Daily end time for the event"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            {/* Time Slots Management */}
            <Grid item xs={12}>
              <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2, mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Time Slots</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addTimeSlot}
                  >
                    Add Slot
                  </Button>
                </Box>
                
                {formData.time_slots.map((slot, index) => (
                  <Card key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Slot Name"
                          value={slot.slot_name}
                          onChange={(e) => updateTimeSlot(index, 'slot_name', e.target.value)}
                          placeholder="e.g., Morning Shift"
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="time"
                          label="Start Time"
                          value={slot.start_time}
                          onChange={(e) => updateTimeSlot(index, 'start_time', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="time"
                          label="End Time"
                          value={slot.end_time}
                          onChange={(e) => updateTimeSlot(index, 'end_time', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={10} sm={10}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Required Volunteers"
                          value={slot.required_volunteers}
                          onChange={(e) => updateTimeSlot(index, 'required_volunteers', parseInt(e.target.value))}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={2} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          color="error"
                          onClick={() => removeTimeSlot(index)}
                          disabled={formData.time_slots.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Card>
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEvent}>
            {eventDialog.event ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventManagement;